import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import DataTable from '../../components/Common/DataTable';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  PlusIcon, PencilIcon, TrashIcon, EyeIcon,
  MagnifyingGlassIcon, FunnelIcon, XMarkIcon, CalendarIcon
} from '@heroicons/react/24/outline';

const Drivers = ({ status }) => {
  const [drivers, setDrivers] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  
  const [filters, setFilters] = useState({
    nameLike: '',
    startedFrom: '',
    active: status === 'active' ? true : status === 'inactive' ? false : null
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const { loading, get, del } = useApi();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    fetchDrivers();
  }, [filters, pageInfo.currentPage, searchTerm]);

  const fetchDrivers = async () => {
    const params = new URLSearchParams();
    
    params.append('page', pageInfo.currentPage);
    params.append('size', pageInfo.size);
    
    if (filters.nameLike) params.append('nameLike', filters.nameLike);
    if (filters.startedFrom) params.append('startedFrom', filters.startedFrom);
    if (filters.active !== null) params.append('active', filters.active);
    
    if (searchTerm && !filters.nameLike) {
      params.append('nameLike', searchTerm);
    }
    
    const endpoint = `/api/drivers/filter?${params.toString()}`;
    
    const { data } = await get(endpoint);
    if (data) {
      setDrivers(data.content || []);
      setPageInfo({
        totalElements: data.totalElements,
        totalPages: data.totalPages,
        currentPage: data.number || 0,
        size: data.size || 10
      });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to deactivate this driver?')) {
      const { error } = await del(`/api/drivers/${id}`);
      if (!error) fetchDrivers();
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchDrivers();
  };

  const clearFilters = () => {
    setFilters({
      nameLike: '',
      startedFrom: '',
      active: status === 'active' ? true : status === 'inactive' ? false : null
    });
    setSearchTerm('');
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  };

  const columns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Full Name', accessor: 'fullName' },
    { header: 'Mobile', accessor: 'mobileNumber' },
    { header: 'Other ID', accessor: 'otherId' },
    { header: 'Address', accessor: (row) => row.address ? `${row.address.substring(0, 30)}...` : '-' },
    { header: 'Country', accessor: 'country' },
    { header: 'State', accessor: 'drvState' },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          row.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
        }`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      header: 'Actions', 
      accessor: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/drivers/${row.id}`)}
            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
            title="View"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {isAdmin && (
            <>
              <button
                onClick={() => navigate(`/drivers/edit/${row.id}`)}
                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                title="Edit"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDelete(row.id)}
                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                title="Deactivate"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Drivers {status && `- ${status.charAt(0).toUpperCase() + status.slice(1)}`}
        </h1>
        <button
          onClick={() => navigate('/drivers/add')}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2 w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Driver</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="mb-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name..."
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Search
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 ${
              showFilters ? 'bg-indigo-50 border-indigo-300 text-indigo-700 dark:bg-indigo-900 dark:border-indigo-700 dark:text-indigo-300' : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Filters</span>
          </button>
          {(filters.nameLike || filters.startedFrom || searchTerm) && (
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 flex items-center space-x-2"
            >
              <XMarkIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          )}
        </form>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <FunnelIcon className="h-4 w-4 mr-1" /> Filters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Name Contains
              </label>
              <input
                type="text"
                name="nameLike"
                value={filters.nameLike}
                onChange={handleFilterChange}
                placeholder="Filter by name"
                className="input-field text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center">
                <CalendarIcon className="h-3 w-3 mr-1" /> Started From
              </label>
              <input
                type="datetime-local"
                name="startedFrom"
                value={filters.startedFrom || ''}
                onChange={handleFilterChange}
                className="input-field text-sm"
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 mr-2 self-center">Quick status:</span>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: true }))}
              className={`px-3 py-1 text-xs rounded-full ${
                filters.active === true 
                  ? 'bg-green-500 text-white' 
                  : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            >
              Active Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: false }))}
              className={`px-3 py-1 text-xs rounded-full ${
                filters.active === false 
                  ? 'bg-red-500 text-white' 
                  : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200'
              }`}
            >
              Inactive Only
            </button>
            <button
              onClick={() => setFilters(prev => ({ ...prev, active: null }))}
              className={`px-3 py-1 text-xs rounded-full ${
                filters.active === null 
                  ? 'bg-gray-500 text-white' 
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              All Drivers
            </button>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400 flex justify-between items-center">
        <span>Showing {drivers.length} of {pageInfo.totalElements} drivers</span>
        {filters.active === true && (
          <span className="px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-xs">
            Active Only
          </span>
        )}
        {filters.active === false && (
          <span className="px-2 py-1 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded-full text-xs">
            Inactive Only
          </span>
        )}
      </div>

      <DataTable 
        columns={columns} 
        data={drivers} 
        pageInfo={pageInfo} 
        onPageChange={(page) => setPageInfo({...pageInfo, currentPage: page})} 
      />
    </div>
  );
};

export default Drivers;